import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import EventCard from '../components/EventCard';
import AdSlot from '../components/AdSlot';
import { searchEvents, getGenres } from '../api/client';
import styles from './Results.module.css';

export default function Results() {
  const [searchParams, setSearchParams] = useSearchParams();
  const lat = parseFloat(searchParams.get('lat') ?? '0');
  const lng = parseFloat(searchParams.get('lng') ?? '0');
  const radius = parseInt(searchParams.get('radius') ?? '5');
  const date = searchParams.get('date') ?? new Date().toISOString().slice(0, 10);
  const locationLabel = searchParams.get('location') ?? 'your location';
  const selectedGenres = searchParams.getAll('genre');

  const eventsQuery = useQuery({
    queryKey: ['events', lat, lng, radius, date, selectedGenres],
    queryFn: () => searchEvents({ lat, lng, radius, date, genre: selectedGenres.length ? selectedGenres : undefined }),
    enabled: !!lat && !!lng,
  });

  const genresQuery = useQuery({
    queryKey: ['genres'],
    queryFn: getGenres,
    staleTime: 5 * 60_000,
  });

  function toggleGenre(g: string) {
    const current = searchParams.getAll('genre');
    const next = current.includes(g) ? current.filter(x => x !== g) : [...current, g];
    const p = new URLSearchParams(searchParams);
    p.delete('genre');
    next.forEach(genre => p.append('genre', genre));
    setSearchParams(p);
  }

  function clearGenres() {
    const p = new URLSearchParams(searchParams);
    p.delete('genre');
    setSearchParams(p);
  }

  const results = eventsQuery.data?.results ?? [];
  const total = eventsQuery.data?.pagination.total ?? 0;
  const genres = genresQuery.data?.genres ?? [];

  const formattedDate = new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  return (
    <div className={styles.page}>
      <div className={`container ${styles.inner}`}>
        {/* Filters sidebar */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <h3 className={styles.sidebarTitle}>Genre</h3>
            {genres.map(g => (
              <label key={g} className={styles.genreCheck}>
                <input
                  type="checkbox"
                  checked={selectedGenres.includes(g)}
                  onChange={() => toggleGenre(g)}
                />
                {g}
              </label>
            ))}
          </div>
          {selectedGenres.length > 0 && (
            <button className={styles.clearBtn} onClick={clearGenres}>Clear filters</button>
          )}
        </aside>

        {/* Main results */}
        <div className={styles.main}>
          <AdSlot type="leaderboard" />

          <div className={styles.resultsHeader}>
            <h2 className={styles.resultsTitle}>
              {eventsQuery.isLoading ? 'Searching…' : `${total} show${total !== 1 ? 's' : ''}`}
            </h2>
            <p className={styles.resultsSub}>
              within {radius} mi of {locationLabel} · {formattedDate}
            </p>
            {selectedGenres.length > 0 && (
              <div className={styles.activeTags}>
                {selectedGenres.map(g => (
                  <span key={g} className={styles.tag}>
                    {g} <button onClick={() => toggleGenre(g)} aria-label={`Remove ${g} filter`}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {eventsQuery.isError && (
            <p className={styles.error}>Failed to load events. Please try again.</p>
          )}

          {!eventsQuery.isLoading && results.length === 0 && !eventsQuery.isError && (
            <div className={styles.empty}>
              <p>No shows found within {radius} miles on {formattedDate}.</p>
              <p>Try a wider radius or a different date.</p>
              <Link to="/suggest">Know a venue we're missing? →</Link>
            </div>
          )}

          <div className={styles.grid}>
            {results.map((event: any, i: number) => (
              <>
                <EventCard key={event.id} {...event} />
                {(i + 1) % 6 === 0 && <div key={`ad-${i}`} className={styles.inlineAd}><AdSlot type="inline" /></div>}
              </>
            ))}
          </div>
        </div>

        {/* Right rail */}
        <div className={styles.rail}>
          <AdSlot type="rectangle" />
        </div>
      </div>
    </div>
  );
}
